import styles from './PricingTable.scss';

import cx from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';

import { useFeatureFlagsContext } from 'src/components/FeatureFlags/FeatureFlags';
import { isSelfServicePurchaseEnabled } from 'src/utils/BundlePricingUtils';
import { PricingTableCTA } from '../PricingTableCTA/PricingTableCTA';
import { PricingTableCell } from '../PricingTableCell/PricingTableCell';
import { PricingTableGroup } from '../PricingTableGroup/PricingTableGroup';
import { PricingTableHeadColumn } from '../PricingTableHeadColumn/PricingTableHeadColumn';
import { PricingTableRowTitle } from '../PricingTableRowTitle/PricingTableRowTitle';
import { PricingTableComponentProps } from '../types';

export const PricingTable: React.FC<PricingTableComponentProps> = ({ bundles = [], categoriesWithFeatures = [], bundlesPricings = [], ...tagFields }) => {
  if (!bundles.length || !categoriesWithFeatures.length) return null;

  const { t } = useTranslation();
  const [inViewStickyTrigger, setInViewStickyTrigger] = React.useState(false);

  const { isFlagEnabled } = useFeatureFlagsContext();
  const isDotcomCartEnabled = isFlagEnabled('use-dotcom-cart');

  return (
    <table className={styles.table}>
      <caption className={styles.tableCaption}>
        <span>{t('pricing-detail-table.caption')}</span>
      </caption>

      <thead className={styles.tableHeadColumn}>
        <tr>
          <td className={cx(styles.tableHeadColumnCell, styles.tableStickyTriggerCell)}>
            <div className={styles.tableStickyTriggerWrapper}>
              <InView onChange={setInViewStickyTrigger}>
                <div className={styles.tableStickyTrigger} />
              </InView>
            </div>
          </td>
          {bundles.map(({ title, description, image, id }, bundleIndex) => (
            <PricingTableHeadColumn
              {...tagFields}
              title={title}
              description={description}
              image={image}
              key={`column-${id}`}
              id={id}
              pricings={bundlesPricings[bundleIndex]}
            />
          ))}
        </tr>
        <tr className={styles.tableButtonRow}>
          <td className={cx(styles.tableButtonCell, { [styles.tableButtonCellBg]: !inViewStickyTrigger })} />
          {bundles.map(({ id, checkoutLink, dcsId }, bundleIndex) => (
            <td key={`button-${id}`} className={cx(styles.tableHeadColumnCell, styles.tableButtonCell)}>
              <PricingTableCTA
                checkoutLink={checkoutLink}
                dcsId={isDotcomCartEnabled ? dcsId : undefined}
                isSelfServicePurchaseEnabled={isDotcomCartEnabled ? isSelfServicePurchaseEnabled(bundlesPricings[bundleIndex]) : true}
              />
            </td>
          ))}
        </tr>
      </thead>

      {categoriesWithFeatures.map(category => (
        <PricingTableGroup
          title={category.title}
          key={category.id}
          id={category.id}
          colSpanCount={bundles.length}
        >
          {category.features.map(feature => (
            <tr key={feature.id} data-test="data-row">
              <PricingTableRowTitle
                image={feature.image}
                title={feature.title}
                description={feature.description}
                categoryId={category.id}
                featureId={feature.id}
                productDetails={feature.product}
              />

              {bundles.map((bundle, index) => {
                const value = feature.values.find(value => value.bundleId === bundle.id);
                const pricings = bundlesPricings[index];
                const { keyedInRate, inPersonRate } = pricings || {};
                return <PricingTableCell value={value} key={index} keyedInRate={keyedInRate} inPersonRate={inPersonRate} categoryId={category.id} featureId={feature.id} bundleId={bundle.id} />;
              })}
            </tr>
          ))}
        </PricingTableGroup>
      ))}
    </table>
  );
};
